import { ErrorMessage, TaAutocomplete, TaInput } from '@components/shared';
import { theme } from '@config/theme';
import useLanguageNavigate from '@hooks/useLanguageNavigate';
import { usePageTitle } from '@hooks/usePageTitle';
import { mdiChevronLeft, mdiChevronRight } from '@mdi/js';
import Icon from '@mdi/react';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { Button } from '@mui/material';
import { mapToDictionary } from '@utils/util.functions';
import { Dictionary, Nullable } from '@utils/util.types';
import { useFormik } from 'formik';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useCrops } from 'services/crops';
import { useCropsStore } from 'services/crops/cropsStore';
import * as Yup from 'yup';
import { FarmBasicsLocation } from './FarmBasicsLocation';

interface CropInfo {
	crop_id: Nullable<Dictionary>;
	crop_area: string;
	crop_unit: Dictionary;
	crop_variety: Nullable<string[]>;
}

const initialValues: CropInfo = {
	crop_id: null,
	crop_area: '',
	crop_unit: {
		name: 'HA',
		id: 'hectares',
	},
	crop_variety: [''],
};

const cropValidationSchema = Yup.object().shape({
	crop_area: Yup.string().required('fieldNameError'),
	crop_unit: Yup.object().required('fieldNameError'),
	crop_id: Yup.object().nullable().required('fieldNameError'),
	crop_varieties: Yup.array().of(Yup.string()).notRequired(),
});

export function FarmBasicsForCrops() {
	const { t } = useTranslation();
	const { crops } = useCropsStore();
	const { navigateWithLanguage } = useLanguageNavigate();

	const cropUnitOptions: Dictionary[] = [
		{
			name: t('unitTypes.m2'),
			id: 'm2',
		},
		{
			name: t('unitTypes.hectares'),
			id: 'hectares',
		},
		{
			name: t('unitTypes.acres'),
			id: 'acres',
		},
	];

	useCrops();
	usePageTitle(`Find Specialist`);

	const cropsDictionary = useMemo(() => mapToDictionary(crops), [crops]);
	const formik = useFormik<CropInfo>({
		initialValues: initialValues,
		validationSchema: cropValidationSchema,
		onSubmit: () => {
			return;
		},
	});

	const handleVarietyChange = (index: number, value: string) => {
		const updatedVarieties = [...(formik.values.crop_variety || [])];

		updatedVarieties[index] = value;
		formik.setFieldValue('crop_variety', updatedVarieties);
	};

	const handleVarietyUpdate = (action: 'add' | 'remove', index?: number) => {
		const updatedVarieties = [...(formik.values.crop_variety || [])];

		if (action === 'add') {
			updatedVarieties.push('');
		} else if (action === 'remove' && index !== undefined) {
			updatedVarieties.splice(index, 1);
		}
		formik.setFieldValue('crop_variety', updatedVarieties);
	};

	const goBack = () => {
		navigateWithLanguage('/farmer/services');
	};

	return (
		<>
			<div className="flex flex-col h-full gap-[20px] relative p-[20px] justify-between  mt-[50px] ">
				<div className="flex flex-col gap-[30px]">
					<form onSubmit={formik.handleSubmit}>
						<div className="flex items-center justify-center sm:mt-0 mt-[20px]">
							<div className="flex flex-wrap  max-w-[500px] gap-[20px]">
								<h6 className="text-dark w-full flex items-center justify-center text-center font-bold text-xl ">
									{t('findSpecialistPage.selectConsultationCrop')}
								</h6>
								<div className="flex flex-wrap w-full">
									<div className="w-full md:w-1/2 md:pr-[10px]">
										<TaAutocomplete
											required
											options={cropsDictionary}
											getOptionLabel={(option) => option.name}
											label={t('crops')}
											value={formik.values.crop_id as Dictionary}
											onChange={(event, value) => formik.setFieldValue('crop_id', value)}
											error={Boolean(formik.touched.crop_id && formik.errors.crop_id)}
										/>
									</div>
									<div className="w-1/2 md:w-1/4 pr-[10px] md:mt-0 mt-[20px]">
										<TaInput
											required
											id="crop_area"
											label={t('myFarm.crop_area')}
											size="small"
											type="number"
											value={formik.values.crop_area}
											onChange={formik.handleChange}
											error={formik.errors.crop_area}
											touched={formik.touched.crop_area}
										/>
									</div>
									<div className="w-1/2 md:w-1/4 md:mt-0 mt-[20px]">
										<TaAutocomplete
											required
											options={cropUnitOptions}
											getOptionLabel={(option) => option.name}
											label={t('unit')}
											value={formik.values.crop_unit as Dictionary}
											onChange={(event, value) => formik.setFieldValue('crop_unit', value)}
											error={Boolean(formik.touched.crop_unit && formik.errors.crop_unit)}
										/>
									</div>
								</div>
								{formik.touched.crop_id && formik.errors.crop_id && (
									<div>
										{formik.touched.crop_id && formik.errors.crop_id && (
											<ErrorMessage
												message={t(formik.errors.crop_id as string, { fieldName: t('crops') })}
											/>
										)}
										{formik.touched.crop_unit && formik.errors.crop_unit && (
											<ErrorMessage
												message={t(formik.errors.crop_unit as string, {
													fieldName: t('myFarm.crop_unit'),
												})}
											/>
										)}
										{formik.touched.crop_area && formik.errors.crop_area && (
											<ErrorMessage
												message={t(formik.errors.crop_area as string, {
													fieldName: t('myFarm.crop_area'),
												})}
											/>
										)}
									</div>
								)}
								<div className="w-full">
									{formik.values?.crop_variety?.map((variety, index) => (
										<div key={index} className="relative flex items-center gap-4 mb-4">
											<div className="relative w-full">
												<TaInput
													id={`crop_variety_${index}`}
													label={t('myFarm.variety')}
													size="small"
													value={variety}
													onChange={(e) => handleVarietyChange(index, e.target.value)}
													error={
														formik.touched.crop_variety &&
														Boolean(formik.errors.crop_variety?.[index])
													}
												/>
												{index > 0 && (
													<div
														className="absolute top-[-10px] right-[-10px] flex items-center justify-center rounded-lg bg-red-500 p-1.5 text-white cursor-pointer h-6 w-6 text-xs"
														onClick={() => handleVarietyUpdate('remove', index)}
													>
														<CloseIcon className="!text-xs" />
													</div>
												)}
											</div>
										</div>
									))}
									<div className="w-full flex justify-start">
										<Button
											className="text-[#343A40] font-black"
											onClick={() => handleVarietyUpdate('add')}
											sx={buttonStyle}
											variant="contained"
											startIcon={<AddIcon />}
										>
											{t('myFarm.addVariety')}
										</Button>
									</div>
								</div>
							</div>
						</div>
					</form>
					<FarmBasicsLocation />
				</div>
				<div className="mb-[32px] flex items-center justify-between gap-[20px] p-[20px]">
					<Button onClick={goBack} startIcon={<Icon path={mdiChevronLeft} size={1} />}>
						{t('previous')}
					</Button>
					<Button
						onClick={() => {
							navigateWithLanguage('/farmer/services/prerequisites/farm-problems');
						}}
						variant="contained"
						endIcon={<Icon path={mdiChevronRight} size={1} />}
					>
						{t('next')}
					</Button>
				</div>
			</div>
		</>
	);
}
const buttonStyle = {
	backgroundColor: theme.palette.custom.contrastTextWhite,
	color: theme.palette.custom.darkGray,
};
